import { Center, Flex, Spinner, Box, Button, Icon } from '@chakra-ui/react';
import { KDrawer } from '@/ui-library/components';
import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Back } from '@/assets/icons/bold';
import { SchedulerDetails } from '@/components/shared/interview-scheduler/SchedulerDetails';
import { Scorecard } from '@/components/shared/interview-scheduler/Scorecard/Container';
import { CalendarInvites } from '@/components/shared/interview-scheduler/CalendarInvites/Container';
import {
  STEP_STATUS_ACTIVE,
  STEP_STATUS_PENDING,
  STEP_STATUS_COMPLETED,
  SCHEDULE_TYPE_MANUAL,
  GET_SCHEDULE_STEPS,
  SCHEDULE_TYPE_SELF,
  SCHEDULE_TYPE_BULK_SELF_SCHEDULE,
  SCHEDULE_TYPE_AI_ADHOC,
} from '@/components/shared/interview-scheduler/constant';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ScheduleAPI } from '@/components/shared/interview-scheduler/api';
import EmailCandidates from '@/components/shared/interview-scheduler/EmailCandidates';
import InterviewToSchedule from '@/components/shared/interview-scheduler/InterviewToSchedule';
import { cloneDeep } from 'lodash-es';
import { KConfirmation } from '@/ui-library/components/molecules';
import { ScheduleHeader } from '@/components/shared/interview-scheduler/components/ScheduleHeader';
import { useShowToast } from '@/hooks/useShowToast';
import { type ApplicationData } from '@/components/shared/interview-scheduler/types/scheduler';
import { getErrorMessage } from '@/utils/errors';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useSelector } from 'react-redux';
import { InfoCircle } from '@kula-ai/icons/outline/InfoCircle';
import '@/components/shared/interview-scheduler/styles/schedule.css';
import moment from 'moment-timezone';
import { TIME_ZONE_CONSTANTS } from '@/utils/timeZoneConstants';
import DOMPurify from 'dompurify';
import { htmlToText } from 'html-to-text';
import { htmlHasFormatting } from '@/utils/textarea-parser';
import { useKFlags } from '@/hooks/use-k-flags';
import { useBulkOperation } from '@/components/ats/BulkOperationQueue';
import {
  useCreateCoordinatorSession,
  COORDINATOR_ERROR_MESSAGES,
  type CreateCoordinatorSessionPayload,
} from '@/api/ai-coordinator';
import {
  isCoordinatorCapError,
  getCoordinatorCapData,
  type CoordinatorCapError,
} from '@/api/ai-coordinator/coordinator-credits';
import { InsufficientCreditsModal } from '@/components/ats/actions/background-check/InsufficientCreditsModal';
import { CoordinatorScheduleStep } from '@/components/ats/job-stage-actions/actions/interview/ScheduleWithAi';
import {
  hasValidCandidateTimezone,
  getPresetScheduleAnchorTimezoneKey,
} from '@/components/ats/job-stage-actions/actions/interview/ScheduleWithAi/scheduleWithAiTimezoneUtils';
import { SCHEDULE_AI_COPY } from '@/components/ats/job-stage-actions/actions/interview/ScheduleWithAi/constants';

interface SchedulerProps {
  scheduleType: 'manual' | 'self' | 'bulk-self' | 'bulk-manual' | 'ai-adhoc';
  isAdhoc: boolean;
  applicationId?: number;
  stageId?: number;
  stageActivityId?: number;
  interviewId?: number;
  apiReference?: string;
  candidateId?: number;
  jobId: number;
  scheduleId?: number;
  onSuccess?: (_?: string) => void;
  closeModal: () => void;
  bulkScheduleProps?: {
    selectedApplications: number[];
    unSelectedApplications: number[];
    jobStageId: number;
    jobStatus: 'active' | 'hired';
  };
  candidate?: any;
  currentJob?: any;
  applicationData?: ApplicationData;
  interviewPlatform?: 'hackerrank' | null;
  candidateAvailabilitySlots?: { start_at: string; end_at: string }[];
}

interface ScheduleDefaultPayload {
  ats_application_id?: number;
  ats_job_id: number;
  ats_job_stage_id: number;
  ats_job_stage_action_id: number | null;
  candidate_id: number;
  ats_application_ids?: string[];
  contact_id?: number;
}

export const Scheduler = ({
  scheduleType = SCHEDULE_TYPE_MANUAL,
  isAdhoc = false,
  stageId = 0,
  stageActivityId = undefined,
  interviewId = 0, // for edit case
  candidateId = 0,
  applicationId = 0,
  scheduleId = 0,
  jobId = 0,
  apiReference = 'create',
  closeModal,
  onSuccess,
  bulkScheduleProps,
  applicationData,
  interviewPlatform = null,
  candidateAvailabilitySlots,
}: SchedulerProps) => {
  // local variables
  const scheduleSteps = cloneDeep(GET_SCHEDULE_STEPS(scheduleType, isAdhoc));
  const SCORECARD_REF = 'INTERVIEW_SCORECARD';
  const [stepItems, setStepItems] = useState(scheduleSteps as Array<any>);
  /** Coordinator schedule step: drives primary CTA ("Call now" vs "Send schedule"). */
  const [coordinatorScheduleTiming, setCoordinatorScheduleTiming] =
    useState('immediately');
  const [coordinatorScheduleIsValid, setCoordinatorScheduleIsValid] =
    useState(true);
  /**
   * Ad-hoc AI details step: track per-interviewer calendar availability so we can
   * disable the primary CTA while /calendar_events is polling and when any
   * selected interviewer has calendar_available === false.
   */
  const [adhocAiCalendarState, setAdhocAiCalendarState] = useState<{
    isLoading: boolean;
    allConnected: boolean;
    hasSelection: boolean;
  }>({ isLoading: false, allConnected: true, hasSelection: false });
  const handleAdhocAiCalendarStatusChange = useCallback(
    (state: {
      isLoading: boolean;
      allConnected: boolean;
      hasSelection: boolean;
    }) => {
      setAdhocAiCalendarState(state);
    },
    []
  );
  const [isLoading, setIsLoading] = useState(true);
  const [contentPrepared, setContentPrepared] = useState(false);
  const [showClearConfirmationModal, setShowClearConfirmationModal] =
    useState(false);
  const [contacts, setContacts] = useState([]);
  const [applicationIds, setApplicationIds] = useState([]); // Use it for bulk self schedule
  const [newStageActivityId, setNewStageActivityId] = useState();
  const [timezoneModalTrigger, setTimezoneModalTrigger] = useState(0);
  const previewDetails = useRef({});
  const queryClient = useQueryClient();
  const toast = useShowToast();
  const currentUser = useSelector(selectCurrUser);
  const { feHtmlToText } = useKFlags();

  const currentStep = useMemo(() => {
    const currentStep =
      stepItems.find((item) => item.status === STEP_STATUS_ACTIVE) || null;
    return currentStep;
  }, [stepItems]);

  const hasScorecardStep = useMemo(
    () => stepItems.some((item) => item.componentRef === SCORECARD_REF),
    [stepItems]
  );
  // overall server payload
  const payload = useRef(null);

  // child refs
  const childRefs = {
    INTERVIEW_DETAILS: useRef(null),
    INTERVIEW_INVITES: useRef(null),
    INTERVIEW_EMAIL_TO_CANDIDATE: useRef(null),
    INTERVIEW_SCORECARD: useRef(null),
    COORDINATOR_SCHEDULE: useRef(null),
  };

  const createCoordinatorSession = useCreateCoordinatorSession();
  // Coordinator credit-cap 402 → insufficient-credits modal.
  const [capError, setCapError] = useState<CoordinatorCapError | null>(null);

  // API calls
  const isCreateMode = apiReference === 'create';
  // Interview Plan create and edit API calls
  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- pre-existing; behavior preserved (extraction move)
  const { data: interviewData, isFetching } = useQuery({
    queryKey: isCreateMode
      ? [`getInterviewPlan/${stageId}/${stageActivityId}`]
      : [`getInterviewDetails/${interviewId}`],
    queryFn: () =>
      isCreateMode
        ? ScheduleAPI?.getInterviewPlan({
            stageId,
            stageActivityId,
            contactId: candidateId,
          })
        : ScheduleAPI?.getInterviewDetails(interviewId),
    staleTime: 0,
    refetchInterval: 0,
    enabled: scheduleType === SCHEDULE_TYPE_MANUAL,
  });

  // Schedule link create and edit API calls
  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- pre-existing; behavior preserved (extraction move)
  const { data: scheduleData, isFetching: isScheduleFetching } = useQuery({
    queryKey: isCreateMode
      ? [`getInterviewPlan/${stageId}/${stageActivityId}`]
      : [`getScheduleDetails/${scheduleId}`],
    queryFn: () =>
      isCreateMode
        ? ScheduleAPI?.getInterviewPlan({
            stageId,
            stageActivityId,
            contactId: candidateId,
          })
        : ScheduleAPI?.getSelfScheduleDetails(scheduleId),
    staleTime: 0,
    refetchInterval: 0,
    enabled: scheduleType === SCHEDULE_TYPE_SELF,
  });

  const handleManualFetch = async (newStageActivityId) => {
    setNewStageActivityId(newStageActivityId);

    // Update the query with the same key that useQuery is watching
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- pre-existing; behavior preserved (extraction move)
    const result = await queryClient.fetchQuery({
      queryKey: isCreateMode
        ? [`getInterviewPlan/${stageId}/${newStageActivityId}`]
        : [`getScheduleDetails/${scheduleId}`],
      queryFn: () =>
        ScheduleAPI?.getInterviewPlan({
          stageId,
          stageActivityId: newStageActivityId,
          contactId: candidateId,
        }),
    });

    // Manually update the query cache with the result to ensure scheduleData gets updated
    queryClient.setQueryData(
      isCreateMode
        ? [`getInterviewPlan/${stageId}/${stageActivityId}`]
        : [`getScheduleDetails/${scheduleId}`],
      result
    );
  };

  const createInterviewMutation = useMutation({
    mutationFn: (data) => ScheduleAPI?.createInterview(data['payload']),
  });

  const updateInterviewMutation = useMutation({
    mutationFn: (data) =>
      ScheduleAPI?.updateInterview(data['payload'], data['interviewId']),
  });

  const createSelfScheduleMutation = useMutation({
    mutationFn: (data) => ScheduleAPI?.createSelfSchedule(data['payload']),
  });

  const updateSelfScheduleMutation = useMutation({
    mutationFn: (data) =>
      ScheduleAPI?.updateSelfSchedule(data['payload'], data['scheduleId']),
  });

  const createBulkSelfScheduleMutation = useMutation({
    mutationFn: (data) => ScheduleAPI?.createBulkSelfSchedule(data['payload']),
  });

  // Bulk operation tracking for self-schedule
  const selfScheduleBulkOp = useBulkOperation({
    // @ts-expect-error - self_schedule is not a valid activity type
    activityType: 'self_schedule',
    title: 'Sending self-schedule links',
  });

  const getServerContext = useMemo(() => {
    const { interview, ...others } = interviewData ?? {};
    const serverData = {
      [SCHEDULE_TYPE_MANUAL]: {
        create: () => ({ ...interview, ...others }),
        edit: () => interviewData,
      },
      [SCHEDULE_TYPE_SELF]: {
        create: () => ({ ...interview, ...others }),
        edit: () => scheduleData,
      },
      [SCHEDULE_TYPE_BULK_SELF_SCHEDULE]: {
        create: () => ({ ...interview, ...others }),
        edit: () => scheduleData,
      },
      [SCHEDULE_TYPE_AI_ADHOC]: {
        create: () => null,
        edit: () => null,
      },
    };
    const typeConfig = serverData[scheduleType] || {};
    return typeConfig[apiReference]?.() ?? null;
  }, [interviewData, scheduleData, scheduleType, apiReference]);

  const interviewPlan = getServerContext;

  useEffect(() => {
    if (interviewPlan && interviewPlan?.scorecard_required === false) {
      setStepItems((prevSteps) => {
        const hasScorecard = prevSteps.some(
          (item) => item.componentRef === SCORECARD_REF
        );
        if (!hasScorecard) return prevSteps;

        const filteredSteps = prevSteps.filter(
          (item) => item.componentRef !== SCORECARD_REF
        );
        return filteredSteps.map((item, index) => {
          const nextItem = filteredSteps[index + 1];
          const prevItem = filteredSteps[index - 1];
          return {
            ...item,
            nextNode: nextItem?.id || null,
            prevNode: prevItem?.id || null,
            ...(!nextItem && {
              submitButtonLabel:
                scheduleType === SCHEDULE_TYPE_SELF ||
                scheduleType === SCHEDULE_TYPE_BULK_SELF_SCHEDULE
                  ? 'Schedule meeting'
                  : 'Schedule interview',
            }),
          };
        });
      });
    } else if (interviewPlan && interviewPlan?.scorecard_required !== false) {
      setStepItems((prevSteps) => {
        const hasScorecard = prevSteps.some(
          (item) => item.componentRef === SCORECARD_REF
        );
        if (hasScorecard) return prevSteps;

        const originalSteps = cloneDeep(
          GET_SCHEDULE_STEPS(scheduleType, isAdhoc)
        );
        const originalHasScorecard = originalSteps.some(
          (item) => item.componentRef === SCORECARD_REF
        );
        if (!originalHasScorecard) return prevSteps;

        return originalSteps.map((step) => {
          const existingStep = prevSteps.find((s) => s.id === step.id);
          if (existingStep) {
            return {
              ...step,
              status: existingStep.status,
              enabled: existingStep.enabled,
            };
          }
          return step;
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interviewPlan?.scorecard_required, scheduleType]);

  // business logic methods
  const initiatePreviousStep = () => {
    // change current step to pending and previous step to active
    const previousNode = currentStep?.prevNode;
    if (!previousNode) return;

    const previousStepItem = stepItems.find((item) => item.id === previousNode);

    const updatedStepItems = stepItems.map((item) => {
      if (item.id === currentStep?.id) {
        return { ...item, status: STEP_STATUS_PENDING };
      }
      if (item.id === previousStepItem?.id) {
        return { ...item, status: STEP_STATUS_ACTIVE };
      }
      return item;
    });

    setStepItems(updatedStepItems);
  };

  const validateAndInitiateNextStep = async () => {
    // validate current step and show errors if any
    // else change current step to completed and next step to active
    const hasValidData = await currentStepHasErrors(currentStep);

    if (!hasValidData) return;

    // Check if any interviewer is missing timezone for self-schedule
    if (
      (scheduleType === SCHEDULE_TYPE_SELF ||
        scheduleType === SCHEDULE_TYPE_BULK_SELF_SCHEDULE) &&
      currentStep?.componentRef === 'INTERVIEW_DETAILS'
    ) {
      // Re-fetch to ensure we have the latest data
      const componentRef = childRefs?.INTERVIEW_DETAILS;
      const modelResult = await componentRef?.current?.getModel?.();

      let interviewersDetails =
        (previewDetails?.current as any)?.interviewersDetails || [];

      // If interviewersDetails is empty but we have participant_ids in payload,
      // wait briefly for data sync and re-fetch
      const participantIds = modelResult?.payload?.participant_ids || [];
      if (interviewersDetails?.length === 0 && participantIds?.length > 0) {
        // Small delay to allow useEffect to sync data
        await new Promise((resolve) => setTimeout(resolve, 100));
        await componentRef?.current?.getModel?.();
        interviewersDetails =
          (previewDetails?.current as any)?.interviewersDetails || [];
      }

      // Validate if we have interviewer details
      if (interviewersDetails?.length > 0) {
        const hasInterviewerWithoutTimezone = interviewersDetails?.some(
          (interviewer: any) => !interviewer?.time_zone
        );
        if (hasInterviewerWithoutTimezone) {
          // Trigger the timezone modal to open
          setTimezoneModalTrigger((prev) => prev + 1);
          return;
        }
      }
    }

    const nextStep = currentStep?.nextNode;
    if (!nextStep) {
      if (currentStep?.componentRef === 'COORDINATOR_SCHEDULE') {
        await saveCoordinatorSchedule();
      } else {
        saveSchedule();
      }
      return;
    }

    const nextStepItem = stepItems.find((item) => item.id === nextStep);

    const updatedStepItems = stepItems.map((item) => {
      if (item.id === currentStep?.id) {
        return { ...item, status: STEP_STATUS_COMPLETED };
      }
      if (item.id === nextStepItem?.id) {
        return { ...item, status: STEP_STATUS_ACTIVE };
      }
      return item;
    });

    setStepItems(updatedStepItems);
  };

  const setPayload = (kStep = {}, updatedPayload = {}) => {
    const key = kStep['id'];
    payload.current = {
      ...payload.current,
      [key]: updatedPayload,
    };
  };

  const currentStepHasErrors = async (kStep) => {
    const componentRef = childRefs[`${kStep['componentRef']}`];
    const componentModel = await componentRef?.current?.getModel();
    if (componentModel?.hasValidData) {
      setPayload(kStep, componentModel?.payload);
    }
    return componentModel?.hasValidData;
  };

  const saveCoordinatorSchedule = async () => {
    const coordinatorModel =
      await childRefs.COORDINATOR_SCHEDULE?.current?.getModel?.();
    if (!coordinatorModel?.hasValidData) return;
    const details = payload.current?.details || {};
    const coordinator = coordinatorModel?.payload || {};
    const rawAtsOfficeId = details.ats_office_id ?? details.ats_office?.id;
    const resolvedAtsOfficeId =
      rawAtsOfficeId !== null && rawAtsOfficeId !== ''
        ? Number(rawAtsOfficeId)
        : null;
    const shouldSendAtsOfficeId =
      details.location === 'onsite' &&
      resolvedAtsOfficeId !== null &&
      Number.isFinite(resolvedAtsOfficeId);
    const participantIds = Array.isArray(details.participant_ids)
      ? details.participant_ids
      : details.participant_ids
        ? [details.participant_ids]
        : [];
    const timing = coordinator.timing || 'immediately';
    const customSchedule = coordinator.customSchedule;
    const userTimezoneKey = currentUser?.time_zone || moment.tz.guess();
    let scheduled_at: string | null;
    if (timing === 'immediately') {
      scheduled_at = null;
    } else if (
      timing === 'custom' &&
      customSchedule?.date &&
      customSchedule?.time &&
      customSchedule?.timezoneKey
    ) {
      scheduled_at = moment
        .tz(
          `${moment(customSchedule.date).format('YYYY-MM-DD')} ${customSchedule.time}`,
          customSchedule.timezoneKey
        )
        .utc()
        .toISOString();
    } else {
      const hasCt = hasValidCandidateTimezone(applicationData?.contact);
      const anchorTz = getPresetScheduleAnchorTimezoneKey(
        hasCt,
        applicationData?.contact?.time_zone
          ? String(applicationData.contact.time_zone)
          : '',
        userTimezoneKey
      );
      const anchor = moment().tz(anchorTz);
      let scheduledMoment: moment.Moment | null = null;
      switch (timing) {
        case 'today_evening':
          scheduledMoment = anchor.clone().startOf('day').add(18, 'hours');
          break;
        case 'tomorrow_morning':
          scheduledMoment = anchor
            .clone()
            .add(1, 'day')
            .startOf('day')
            .add(9, 'hours');
          break;
        case 'tomorrow_afternoon':
          scheduledMoment = anchor
            .clone()
            .add(1, 'day')
            .startOf('day')
            .add(14, 'hours');
          break;
        case 'tomorrow_evening':
          scheduledMoment = anchor
            .clone()
            .add(1, 'day')
            .startOf('day')
            .add(18, 'hours');
          break;
        default:
          scheduledMoment = null;
      }
      scheduled_at =
        scheduledMoment !== null ? scheduledMoment.utc().toISOString() : null;
    }
    const coordinatorPayload: CreateCoordinatorSessionPayload = {
      subject_type: 'ats_application',
      subject_id: applicationId,
      ats_job_stage_id: stageId,
      ats_job_stage_action_id: null,
      interview_name: details.name || 'Ad-hoc interview',
      interview_kind: details.kind === 'panel' ? 'panel' : 'one_on_one',
      duration_minutes: details.duration || 60,
      location: (details.location ||
        'google_meet') as CreateCoordinatorSessionPayload['location'],
      ...(shouldSendAtsOfficeId && { ats_office_id: resolvedAtsOfficeId }),
      participant_ids: participantIds,
      session_type: 'schedule',
      scheduled_at,
      ...(coordinator.coordinator_template_id !== undefined &&
        coordinator.coordinator_template_id !== null && {
          coordinator_template_id: coordinator.coordinator_template_id,
        }),
    };
    createCoordinatorSession.mutate(coordinatorPayload, {
      onSuccess: (data: any) => {
        if (isCoordinatorCapError(data)) {
          setCapError(getCoordinatorCapData(data));
          return;
        }
        const errors = data?.errors;
        const hasError =
          Array.isArray(errors) && errors.length > 0 ? errors : null;
        if (hasError) {
          const code = hasError[0];
          const message =
            COORDINATOR_ERROR_MESSAGES[code] ||
            'Failed to schedule with AI. Please try again.';
          toast({ title: message, status: 'error' });
          return;
        }
        onSuccess?.(apiReference);
        closeModal();
      },
      onError: (err: any) => {
        if (isCoordinatorCapError(err)) {
          setCapError(getCoordinatorCapData(err));
          return;
        }
        const errors = err?.response?.data?.errors;
        const code = Array.isArray(errors) ? errors[0] : errors;
        const message =
          (code && COORDINATOR_ERROR_MESSAGES[code]) ||
          'Failed to schedule with AI. Please try again.';
        toast({ title: message, status: 'error' });
      },
    });
  };

  const saveSchedule = () => {
    const defaultPayload: ScheduleDefaultPayload = {
      ats_application_id: applicationId,
      ats_job_id: jobId,
      ats_job_stage_id: stageId,
      ats_job_stage_action_id: stageActivityId || null,
      candidate_id: candidateId,
    };
    // using requestAnimationFrame in React to get the latest state value
    // This is needed as the state update might not be processed by React yet when we make an API call
    requestAnimationFrame(() => {
      let preparedPayload = { ...defaultPayload };
      Object.keys(payload.current).forEach((key) => {
        preparedPayload = { ...preparedPayload, ...payload.current[key] };
      });
      switch (scheduleType) {
        case SCHEDULE_TYPE_MANUAL:
          handleInterviewCreation(preparedPayload);
          break;
        case SCHEDULE_TYPE_SELF:
          handleSelfScheduleCreation(preparedPayload);
          break;
        default:
          handleBulkSelfScheduleCreation(preparedPayload);
      }
    });
  };

  const showScheduleErrorMessage = (error) => {
    toast({
      title: error,
      status: 'error',
    });
  };

  const convertHtmlToTextWhenUnformatted = (
    keysToBeChecked: string[],
    payload: any
  ) => {
    // Utility: Since htmlToText adds extra newlines like (/n/n/n/n), we need to collapse them to 2 newlines
    // replace 2+ newlines with just 2 newlines
    const collapseNewlines = (str) => str.replace(/\n{2,}/g, '\n\n');

    keysToBeChecked.forEach((key) => {
      if (feHtmlToText && !htmlHasFormatting(payload[key])) {
        payload[key] = collapseNewlines(htmlToText(payload[key]));
      }
    });
    return payload;
  };

  const handleInterviewCreation = async (preparedPayload) => {
    if (feHtmlToText) {
      preparedPayload = convertHtmlToTextWhenUnformatted(
        ['candidate_description', 'interviewer_description'],
        preparedPayload
      );
    }

    const mutation =
      apiReference === 'create'
        ? createInterviewMutation
        : updateInterviewMutation;

    const result = await mutation.mutateAsync({
      payload: preparedPayload,
      interviewId,
    } as any);

    if (result?.error?.length) {
      showScheduleErrorMessage(getErrorMessage(result, 'scheduler'));
    } else {
      onSuccess(apiReference);
    }
  };

  const handleSelfScheduleCreation = async (preparedPayload) => {
    if (feHtmlToText) {
      preparedPayload = convertHtmlToTextWhenUnformatted(
        ['candidate_description', 'interviewer_description'],
        preparedPayload
      );
    }

    preparedPayload['interview_kind'] = preparedPayload['kind'];
    preparedPayload['contact_id'] = preparedPayload['candidate_id'];
    const mutation =
      apiReference === 'create'
        ? createSelfScheduleMutation
        : updateSelfScheduleMutation;

    const result = await mutation.mutateAsync({
      payload: preparedPayload,
      scheduleId,
    } as any);

    if (result?.error?.length) {
      showScheduleErrorMessage(getErrorMessage(result, 'scheduler'));
    } else {
      onSuccess(apiReference);
    }
  };

  const handleBulkSelfScheduleCreation = async (preparedPayload) => {
    if (feHtmlToText) {
      preparedPayload = convertHtmlToTextWhenUnformatted(
        ['candidate_description', 'interviewer_description'],
        preparedPayload
      );
    }
    preparedPayload['interview_kind'] = preparedPayload['kind'];
    preparedPayload['contact_id'] = preparedPayload['candidate_id'];
    const {
      ats_application_id: _ats_application_id,
      kind,
      interview_kind,
      ...restPayload
    } = preparedPayload;

    const totalCandidates = applicationIds?.length || 1;

    const updatedPayload = {
      ...restPayload,
      ats_application_ids: applicationIds,
      ats_job_stage_action_id: newStageActivityId,
      interview_kind: kind || interview_kind,
      calendar_visibility: preparedPayload?.calendar_visibility,
      is_bulk_activity: true,
      source_job_id: jobId,
    };

    // Start tracking and close modal immediately
    selfScheduleBulkOp.startTracking(totalCandidates);
    closeModal(); // Close modal immediately - bulk operation queue will show progress

    const result = await createBulkSelfScheduleMutation.mutateAsync({
      payload: updatedPayload,
    } as any);

    if (result?.error?.length) {
      selfScheduleBulkOp.completeWithErrors({
        successCount: 0,
        failedCount: totalCandidates,
        errors: result.error.map((e: any) => ({
          id: e.id || 0,
          message: e.message || 'Error',
        })),
      });
      showScheduleErrorMessage(getErrorMessage(result, 'scheduler'));
    } else {
      selfScheduleBulkOp.completeSuccess(totalCandidates);
      // Don't call onSuccess for bulk operations - no toast needed
      // The bulk operation queue handles the feedback
    }
  };

  const initiateKStepSelection = (selectedItem) => {
    const selectedIndex = selectedItem['index'];
    const { status } = selectedItem['step'];
    if (status === STEP_STATUS_ACTIVE) return;
    if (status === STEP_STATUS_PENDING) {
      initiateKStepPendingClick(selectedItem['step']);
      return;
    }
    const updatedStepItems = stepItems.map((item, index) => {
      if (index === selectedIndex) {
        return { ...item, status: STEP_STATUS_ACTIVE };
      } else if (index > selectedIndex) {
        return { ...item, status: STEP_STATUS_PENDING };
      } else {
        return item;
      }
    });
    setStepItems(updatedStepItems);
  };

  const initiateKStepPendingClick = async (selectedItem) => {
    // Find the currently active step to compare against later
    const currentActiveStep = stepItems.find(
      (item) => item.status === STEP_STATUS_ACTIVE
    );

    // Get the index of the step the user clicked on
    const selectedIndex = stepItems.findIndex(
      (item) => item.componentRef === selectedItem.componentRef
    );

    // Performance optimization: If the clicked step is already active, skip any updates
    if (currentActiveStep?.id === selectedItem.id) {
      return;
    }

    // Find the INTERVIEW_DETAILS step index
    const interviewDetailsIndex = stepItems.findIndex(
      (item) => item.componentRef === 'INTERVIEW_DETAILS'
    );

    // Check timezone validation if navigating past INTERVIEW_DETAILS step
    if (
      (scheduleType === SCHEDULE_TYPE_SELF ||
        scheduleType === SCHEDULE_TYPE_BULK_SELF_SCHEDULE) &&
      selectedIndex > interviewDetailsIndex &&
      currentActiveStep?.componentRef === 'INTERVIEW_DETAILS'
    ) {
      // Re-fetch to ensure we have the latest data
      const componentRef = childRefs?.INTERVIEW_DETAILS;
      const modelResult = await componentRef?.current?.getModel?.();

      let interviewersDetails =
        (previewDetails?.current as any)?.interviewersDetails || [];

      // If interviewersDetails is empty but we have participant_ids in payload,
      // wait briefly for data sync and re-fetch
      const participantIds = modelResult?.payload?.participant_ids || [];
      if (interviewersDetails?.length === 0 && participantIds?.length > 0) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        await componentRef?.current?.getModel?.();
        interviewersDetails =
          (previewDetails?.current as any)?.interviewersDetails || [];
      }

      // Validate if we have interviewer details
      if (interviewersDetails?.length > 0) {
        const hasInterviewerWithoutTimezone = interviewersDetails?.some(
          (interviewer: any) => !interviewer?.time_zone
        );
        if (hasInterviewerWithoutTimezone) {
          // Trigger the timezone modal to open
          setTimezoneModalTrigger((prev) => prev + 1);
          return;
        }
      }
    }

    // Limit validation to steps up to and including the selected step to enforce sequential order
    const stepsToValidate = stepItems.slice(0, selectedIndex + 1);

    // Validate all steps up to the selected one asynchronously, ensuring we wait for all results
    const responses = await Promise.all(
      stepsToValidate.map(async (item) => {
        return await currentStepHasErrors(item); // Returns true if step is valid, false if invalid
      })
    );

    // Find the index of the first invalid step (if any); -1 means all steps are valid
    const failedIndex = responses.findIndex((response) => !response);

    // Determine the target step: move to the selected step if all prior steps are valid,
    // otherwise move to the first invalid step
    const targetIndex = failedIndex === -1 ? selectedIndex : failedIndex;

    // Performance optimization: If the target step is already active, skip the update
    // This prevents redundant re-renders (e.g., clicking Step 4 when Step 2 is active and invalid)
    if (
      currentActiveStep &&
      stepItems[targetIndex].id === currentActiveStep.id
    ) {
      return;
    }

    // Update the step statuses based on the target index
    const updatedStepItems = stepItems.map((item, index) => {
      if (index < targetIndex) {
        // All steps before the target are marked as completed
        return { ...item, status: STEP_STATUS_COMPLETED, enabled: true };
      } else if (index === targetIndex) {
        // The target step becomes active
        return { ...item, status: STEP_STATUS_ACTIVE, enabled: true };
      } else {
        // Steps after the target remain pending and disabled
        return { ...item, status: STEP_STATUS_PENDING, enabled: false };
      }
    });

    // Apply the updated step statuses to the state
    setStepItems(updatedStepItems);
  };

  const previewPayload = useMemo(() => {
    const payload = {
      contact_id: applicationData?.contact?.id,
      ats_job_id: applicationData?.['job_id'] || jobId,
      sender_id: currentUser.id,
      ats_job_stage_id: stageId,
    };
    return payload;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageId]);

  const handleStartSelfSchedule = ({
    applicationIds,
    actionId,
  }: {
    applicationIds: string[];
    actionId: string | undefined;
  }) => {
    setApplicationIds(applicationIds);
    if (actionId) handleManualFetch(actionId);
  };

  const getPreviewDetails = useCallback(() => {
    return {
      candidate: createPreviewDetails({
        previewDetails,
        payload: payload.current,
        isInterviewer: false,
      }),
      interviewer: createPreviewDetails({
        previewDetails,
        payload: payload.current,
        isInterviewer: true,
      }),
    };
  }, [previewDetails, payload]);

  // render Components
  const renderHeader = () => {
    return (
      <ScheduleHeader
        scheduleType={scheduleType}
        stepItems={
          Array.isArray(stepItems)
            ? stepItems?.map(({ enabled: _enabled, ...otherItems }) => ({
                //don't: send enabled, always have it enabled here
                ...otherItems,
              }))
            : []
        }
        onSelect={initiateKStepSelection}
        candidateId={candidateId}
        contacts={contacts}
        candidate={applicationData?.contact || {}}
        currentJob={applicationData?.ats_job || {}}
        applicationData={applicationData}
        interviewPlatform={interviewPlatform}
      />
    );
  };

  const renderClearConfirmationModal = () => {
    return (
      showClearConfirmationModal && (
        <KConfirmation
          title={`Are you sure you want to clear?`}
          subTitle="All the saved changes will be lost"
          onClose={() => {
            setShowClearConfirmationModal(false);
          }}
          onSubmit={() => {
            childRefs.INTERVIEW_INVITES.current?.getModel();
          }}
          submitButtonLabel="Confirm"
          submitButtonIsLoading={false}
          showDeleteInput={false}
        />
      )
    );
  };

  const renderFooter = () => {
    const coordinatorImmediateLabel = (
      currentStep as
        | { submitButtonLabelWhenTimingImmediate?: string }
        | undefined
    )?.submitButtonLabelWhenTimingImmediate;
    /**
     * Ad-hoc AI details step: block advancing while /calendar_events is polling
     * or when any selected interviewer has calendar_available === false.
     * Swap the CTA label to a "Checking calendars..." spinner during the fetch
     * so users know something is in flight.
     */
    const isAdhocAiDetailsStep =
      scheduleType === SCHEDULE_TYPE_AI_ADHOC &&
      currentStep?.componentRef === 'INTERVIEW_DETAILS';
    const adhocAiDetailsCalendarLoading =
      isAdhocAiDetailsStep && adhocAiCalendarState.isLoading;
    const adhocAiDetailsCalendarBlocked =
      isAdhocAiDetailsStep &&
      adhocAiCalendarState.hasSelection &&
      !adhocAiCalendarState.allConnected;
    const primaryCtaLabel =
      currentStep?.componentRef === 'COORDINATOR_SCHEDULE' &&
      coordinatorScheduleTiming === 'immediately' &&
      coordinatorImmediateLabel
        ? coordinatorImmediateLabel
        : currentStep?.submitButtonLabel || 'Next';

    return (
      <Flex
        justifyContent={'space-between'}
        borderTop="1px solid"
        borderColor="border-color"
        padding={'15px 20px'}
      >
        <Box className="flex">
          {/* <Button
            variant="quaternary"
            // onClick={() => {
            //   setShowClearConfirmationModal(true);
            // }}
            onClick={closeModal}
          >
            Cancel
          </Button> */}
        </Box>
        <Box className="flex justify-end">
          {currentStep?.prevNode && (
            <Button
              mr="10px"
              variant="secondary"
              onClick={initiatePreviousStep}
              data-test-id="secondary-cta-button"
            >
              <Icon
                as={Back}
                color="primary-500"
                width="14px"
                height="14px"
                mr="5px"
                ml="-5px"
              />
              Back
            </Button>
          )}
          <Button
            minW="170px"
            textAlign={'center'}
            variant="primary"
            onClick={validateAndInitiateNextStep}
            isDisabled={
              (currentStep?.componentRef === 'COORDINATOR_SCHEDULE' &&
                !coordinatorScheduleIsValid) ||
              adhocAiDetailsCalendarLoading ||
              adhocAiDetailsCalendarBlocked
            }
            isLoading={
              createInterviewMutation.isPending ||
              updateInterviewMutation.isPending ||
              createSelfScheduleMutation.isPending ||
              updateSelfScheduleMutation.isPending ||
              createBulkSelfScheduleMutation.isPending ||
              (currentStep?.componentRef === 'COORDINATOR_SCHEDULE' &&
                createCoordinatorSession.isPending)
            }
            data-test-id="primary-cta-button"
          >
            {adhocAiDetailsCalendarLoading ? (
              <Flex align="center" gap="8px">
                <Spinner size="xs" />
                {SCHEDULE_AI_COPY.checkingCalendars}
              </Flex>
            ) : (
              primaryCtaLabel
            )}
          </Button>
        </Box>
      </Flex>
    );
  };

  const renderDefaultSpinner = () => {
    return (
      isLoading && (
        <Center h="100%" w="100%">
          <Spinner color="primary-800" />
        </Center>
      )
    );
  };

  const renderCurrentStepComponent = () => {
    const containerHeight =
      document
        .getElementById('schedule-content-container')
        ?.getBoundingClientRect().height || 0;
    return (
      !isLoading && (
        <Flex
          transition="0.2s ease-in opacity"
          opacity={contentPrepared ? 1 : 0}
          w="100%"
          h="100%"
          flex={1}
          direction="column"
          overflow="scroll"
        >
          <Box
            overflow="hidden"
            visibility={
              currentStep?.componentRef === 'INTERVIEW_DETAILS'
                ? 'visible'
                : 'hidden'
            }
            height={
              !(currentStep?.componentRef === 'INTERVIEW_DETAILS') && '0px'
            }
            display={'flex'}
            flex={currentStep?.componentRef === 'INTERVIEW_DETAILS' && 1}
          >
            <SchedulerDetails
              interviewPlan={interviewPlan}
              jobId={jobId}
              jobStageActivityId={stageActivityId}
              variant={
                scheduleType === SCHEDULE_TYPE_AI_ADHOC
                  ? 'manual'
                  : scheduleType
              }
              isEdit={apiReference === 'edit'}
              ref={childRefs.INTERVIEW_DETAILS}
              isAdhoc={isAdhoc}
              bulkScheduleProps={bulkScheduleProps}
              previewDetails={previewDetails}
              candidate={applicationData?.contact}
              contacts={contacts}
              interviewPlatform={interviewPlatform}
              timezoneModalTrigger={timezoneModalTrigger}
              showScorecardStep={hasScorecardStep}
              formOnly={scheduleType === SCHEDULE_TYPE_AI_ADHOC}
              candidateAvailabilitySlots={candidateAvailabilitySlots}
              onCalendarStatusChange={
                scheduleType === SCHEDULE_TYPE_AI_ADHOC
                  ? handleAdhocAiCalendarStatusChange
                  : undefined
              }
            />
          </Box>
          <Box
            display={
              currentStep?.componentRef === 'INTERVIEW_EMAIL_TO_CANDIDATE'
                ? 'flex'
                : 'none'
            }
            flex={1}
          >
            <EmailCandidates
              containerHeight={containerHeight}
              emailCandidateData={interviewPlan?.candidate_mail_comm_template}
              senderData={{
                // note: give preference	 to shared email if present
                from:
                  interviewPlan?.ats_shared_email?.id ||
                  interviewPlan?.candidate_mail_from?.id,
                cc: interviewPlan?.candidate_mail_cc?.map(
                  (contact) => contact?.email
                ),
                bcc: interviewPlan?.candidate_mail_bcc?.map(
                  (contact) => contact?.email
                ),
                ...(interviewPlan?.ats_shared_email?.id && {
                  ats_shared_email: {
                    ...interviewPlan?.ats_shared_email,
                    isSharedEmail: true,
                  },
                }),
              }}
              ref={childRefs.INTERVIEW_EMAIL_TO_CANDIDATE}
              previewPayload={previewPayload}
              isCurrentIndex={
                currentStep?.componentRef === 'INTERVIEW_EMAIL_TO_CANDIDATE'
              }
              previewDetails={getPreviewDetails().candidate}
            />
          </Box>
          <>
            <Box
              bg="primary-25"
              color="secondary-700"
              borderBottom="1px solid"
              borderColor="secondary-100"
              fontSize={'14px'}
              p="11px"
              display={
                currentStep?.componentRef === 'INTERVIEW_INVITES' &&
                scheduleType !== SCHEDULE_TYPE_MANUAL
                  ? 'flex'
                  : 'none'
              }
            >
              <Icon
                cursor="pointer"
                color="secondary-700"
                h="20px"
                w="20px"
                as={InfoCircle}
                mr="10px"
                ml="10px"
              />
              When the candidate picks a slot, we will schedule the interview
              and send out the following calendar invites
            </Box>
            <Box
              display={
                currentStep?.componentRef === 'INTERVIEW_INVITES'
                  ? 'flex'
                  : 'none'
              }
              flex={1}
            >
              <CalendarInvites
                interviewPlan={interviewPlan}
                containerHeight={containerHeight}
                previewPayload={previewPayload}
                ref={childRefs.INTERVIEW_INVITES}
                scheduleType={scheduleType}
                previewDetails={getPreviewDetails()}
              />
            </Box>
          </>
          {hasScorecardStep && (
            <Box
              display={
                currentStep?.componentRef === SCORECARD_REF ? 'flex' : 'none'
              }
              justifyContent="center"
              flex={1}
            >
              <Scorecard
                interviewPlan={interviewPlan}
                ref={childRefs.INTERVIEW_SCORECARD}
                schedulerDetailsRef={childRefs.INTERVIEW_DETAILS}
                isScorecardStepActive={
                  currentStep?.componentRef === SCORECARD_REF
                }
              />
            </Box>
          )}
          {currentStep?.componentRef === 'COORDINATOR_SCHEDULE' && (
            <Box flex={1} flexDirection="column" display="flex">
              <CoordinatorScheduleStep
                ref={childRefs.COORDINATOR_SCHEDULE}
                {...({
                  applicationData,
                  onTimingChange: setCoordinatorScheduleTiming,
                  onValidityChange: setCoordinatorScheduleIsValid,
                } as React.ComponentProps<typeof CoordinatorScheduleStep>)}
              />
            </Box>
          )}
        </Flex>
      )
    );
  };

  useEffect(() => {
    // Setting artificial delay for smooth slider & opacity transition experience
    const loadContentWithDelay = async () => {
      if (scheduleType === SCHEDULE_TYPE_MANUAL && !isFetching) return;
      if (
        scheduleType ===
          (SCHEDULE_TYPE_SELF || SCHEDULE_TYPE_BULK_SELF_SCHEDULE) &&
        !isScheduleFetching
      )
        return;

      await new Promise((resolve) => setTimeout(resolve, 500)); // slider transition time
      setIsLoading(false);

      await new Promise((resolve) => setTimeout(resolve, 0)); // Moving opacity transition time to next thread
      setContentPrepared(true);
    };

    loadContentWithDelay();
    // eslint-disable-next-line
  }, [isFetching, isScheduleFetching]);

  const onContactsChange = useCallback((newContacts) => {
    setContacts(newContacts);
  }, []);

  const showBulSelfSchedule =
    scheduleType === SCHEDULE_TYPE_BULK_SELF_SCHEDULE &&
    applicationIds?.length === 0;

  return (
    <>
      <KDrawer
        isOpen={true}
        onClose={closeModal}
        handleOutsideClose={() => {}}
        variant="full"
        header={renderHeader()}
        footer={!showBulSelfSchedule && renderFooter()}
      >
        <Flex
          id="schedule-content-container"
          w="100%"
          h="100%"
          flex={1}
          overflow="hidden"
        >
          {showBulSelfSchedule ? (
            <InterviewToSchedule
              startSelfSchedule={handleStartSelfSchedule}
              bulkScheduleProps={bulkScheduleProps}
              onContactsChange={onContactsChange}
            />
          ) : (
            <>
              {renderDefaultSpinner()}
              {renderCurrentStepComponent()}
            </>
          )}
        </Flex>
        {renderClearConfirmationModal()}
      </KDrawer>

      <InsufficientCreditsModal
        variant="coordinator"
        isOpen={!!capError}
        onClose={() => setCapError(null)}
        capError={capError}
      />
    </>
  );
};

// Constants for Google Maps integration
const GOOGLE_MAPS_BASE_URL = 'https://www.google.com/maps/place/?q=place_id:';
const MAPS_LINK_TEXT = 'View on maps';
const MAPS_LINK_STYLES = 'color: primary-500; text-decoration: underline;';

// Common function for generating Google Maps link with location
const generateLocationWithMapsLink = (office, isInterviewer = false) => {
  const { name, location, place_id } = office;
  if (!location || !place_id) return '{{interview.onsite_location}}';
  const label = isInterviewer ? `${name}, ${location}` : location;
  if (!label) return '{{interview.onsite_location}}';

  return `${label} (<a href="${GOOGLE_MAPS_BASE_URL}${place_id}" target="_blank" rel="noopener noreferrer" style="${MAPS_LINK_STYLES}">${MAPS_LINK_TEXT}</a>)`;
};

// eslint-disable-next-line react-refresh/only-export-components
export const createPreviewDetails = ({
  previewDetails,
  payload,
  dateFormat = { label: 'MMM DD, YYYY' }, // Default dateFormat
  timeFormat = 'h:mm A', // Default timeFormat
  interviewPlan = null, // For bulk manual mode
  timezone = null, // For bulk manual mode
  isInterviewer = false, // Flag to indicate if it's for interviewer calendar invite
}) => {
  const interviewersDetails =
    previewDetails?.current?.interviewersDetails || [];
  const primaryInterviewer = interviewersDetails?.[0] || {};
  const allInterviewerNames = interviewersDetails
    .map((i: any) => i.name)
    .filter(Boolean)
    .join(', ');
  const office = previewDetails?.current?.['ats_office'] || {};
  const getValueOrFallback = (value: any, fallback: string) =>
    value ?? fallback;

  // Use either payload.details (normal scheduler) or interviewPlan (bulk manual)
  const details = payload?.details || interviewPlan;
  const effectiveTimezone = details?.timezone || timezone;

  const timeZoneAbbr =
    TIME_ZONE_CONSTANTS?.[effectiveTimezone]?.abbreviation || 'UTC';
  const interviewTime = details?.start_time
    ? moment
        .tz(details.start_time, effectiveTimezone)
        .format(`${dateFormat?.label}, ${timeFormat}`)
    : '';

  // Generate location based on whether it's for interviewer or candidate
  const onsiteLocation =
    details?.location === 'onsite'
      ? getValueOrFallback(
          DOMPurify.sanitize(
            generateLocationWithMapsLink(office, isInterviewer)
          ),
          '{{interview.onsite_location}}'
        )
      : '{{interview.onsite_location}}';

  return {
    '{{interview.name}}': getValueOrFallback(
      details?.name,
      '{{interview.name}}'
    ),
    '{{interview.time}}': details?.start_time
      ? `${interviewTime} ${timeZoneAbbr}`
      : '{{interview.time}}',
    '{{interview.duration}}': details?.duration
      ? `${details.duration} minutes`
      : '{{interview.duration}}',
    '{{interview.interviewer_full_name}}': getValueOrFallback(
      primaryInterviewer?.name,
      '{{interview.interviewer_full_name}}'
    ),
    '{{interview.onsite_location}}': onsiteLocation,
    '{{interview.all_interviewers_full_names}}': getValueOrFallback(
      allInterviewerNames,
      '{{interview.all_interviewers_full_names}}'
    ),
  };
};
